define('GeneralInquiry.ei1.Main.View', [
    'SCView',
    'general_inquiry.tpl',
    'GeneralInquiry.ei1.Main.Model'
], function (
    SCViewComponent,
    general_inquiry_tpl,
    GeneralInquiryModel
) {
    'use strict';

    var SCView = SCViewComponent.SCView;

    // Loose validation only - catches obviously-malformed input before it
    // reaches the backend, not full RFC/E.164 compliance. The backend
    // Model re-checks these as a backstop in case JS is bypassed.
    var EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    var PHONE_REGEX = /^[0-9()+\-.\s]{7,20}$/;

    // *** REPLACE with your real reCAPTCHA v3 site key (public - safe to
    // ship in frontend JS). Get one at https://www.google.com/recaptcha/admin
    // registered for shop.1homeoffice.com, reCAPTCHA v3 type. Until this
    // is a real key, Google's script will fail to load/execute and
    // reCAPTCHA is effectively skipped client-side (the backend's own
    // placeholder check handles that gracefully too - see the Model.js
    // SuiteScript2 file).
    var RECAPTCHA_SITE_KEY = '6LfWpr4tAAAAAH_BqwL7-1XMFTpIoRKrSamXbesB';
    var RECAPTCHA_ACTION = 'general_inquiry_submit';

    var recaptchaScriptPromise = null;

    // Lazily injects Google's reCAPTCHA v3 script exactly once (regardless
    // of how many times the form is submitted or re-rendered), and
    // resolves once window.grecaptcha is actually ready to use. Loaded as
    // a plain <script> tag rather than an AMD module, since it's a
    // third-party global-exposing script, not AMD-compatible.
    //
    // NOTE: if the site's Content Security Policy blocks script sources
    // other than the ones already allow-listed for this NetSuite account,
    // this script will fail to load - that's a NetSuite/SMT-side setting
    // (Content Security Policy / allowed domains), not something fixable
    // from this file. If reCAPTCHA silently never triggers, check that
    // first before assuming a code bug.
    function loadRecaptchaScript() {
        if (recaptchaScriptPromise) { return recaptchaScriptPromise; }

        recaptchaScriptPromise = new jQuery.Deferred();

        if (window.grecaptcha && window.grecaptcha.execute) {
            recaptchaScriptPromise.resolve();
            return recaptchaScriptPromise;
        }

        var script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?render=' + RECAPTCHA_SITE_KEY;
        script.async = true;
        script.onload = function () {
            if (window.grecaptcha && window.grecaptcha.ready) {
                window.grecaptcha.ready(function () { recaptchaScriptPromise.resolve(); });
            } else {
                recaptchaScriptPromise.reject();
            }
        };
        script.onerror = function () { recaptchaScriptPromise.reject(); };
        document.head.appendChild(script);

        return recaptchaScriptPromise;
    }

    // Hardcoded sub-option lists for the Space Category values that have
    // a secondary dropdown. Workstation has no secondary dropdown at all,
    // so it simply has no entry here.
    var ROOM_ALCOVE_OPTIONS = {
        'Meeting Room': ['Carrara', 'Trenza Marmi', 'Boston Stone', 'Courtyard'],
        'Alcove': ['Palladio', 'Pietra', 'Travertine', 'Serpentine', 'Foyer'],
        'Events': ['Inner Courtyard', 'Outer Courtyard']
    };

    var SECONDARY_LABELS = {
        'Meeting Room': { label: 'Select Meeting Room', placeholder: 'Choose Meeting Room' },
        'Alcove': { label: 'Select Alcove', placeholder: 'Choose Alcove' },
        'Events': { label: 'Select Event Space', placeholder: 'Choose Event Space' }
    };

    // ===== ANALYTICS (GTM / GA4) =====
    // Pushes a custom event to GTM's dataLayer. GTM's built-in "Form
    // Submission" trigger can't see this form (it's an AJAX save inside
    // the SPA, never a native <form> submit), so GTM listens for these
    // custom events instead via a Custom Event trigger. Same event names
    // as the Contact Us extension; form_name tells them apart.
    //
    // Never push PII here (name, email, phone, city, zip, comments text) -
    // Google Analytics' terms prohibit it. Only non-identifying context.
    var FORM_NAME = 'general_inquiry';

    function pushDataLayer(eventName, params) {
        if (typeof window === 'undefined') { return; } // SSP/SEO safety
        try {
            window.dataLayer = window.dataLayer || [];
            var payload = { event: eventName, form_name: FORM_NAME, form_location: window.location.pathname };
            if (params) {
                for (var k in params) {
                    if (Object.prototype.hasOwnProperty.call(params, k)) { payload[k] = params[k]; }
                }
            }
            window.dataLayer.push(payload);
        } catch (err) { /* tracking must never break the form */ }
    }

    var self = null;

    function isEmpty(value) {
        return value === undefined || value === null || String(value).trim() === '';
    }

    function hasSecondaryDropdown(spaceCategory) {
        return Object.prototype.hasOwnProperty.call(ROOM_ALCOVE_OPTIONS, spaceCategory);
    }

    // "yyyy-mm-dd" built from LOCAL date components (getFullYear/getMonth/
    // getDate), never via toISOString(). toISOString() converts to UTC,
    // which can shift the date by a day depending on the visitor's local
    // timezone and time of day - the exact bug that broke the backend's
    // date handling earlier in this project. Built with plain string
    // concatenation (no String.prototype.padStart) to stay ES5-safe,
    // matching the rest of this codebase's target.
    function formatLocalDate(d) {
        var year = d.getFullYear();
        var month = d.getMonth() + 1;
        var day = d.getDate();
        return year + '-' + (month < 10 ? '0' + month : String(month)) + '-' + (day < 10 ? '0' + day : String(day));
    }

    // Parses whatever a date input's .val() actually returns into a real
    // Date, without assuming it's strictly ISO "yyyy-mm-dd" - in practice
    // that assumption has not held reliably in this environment. If it
    // IS that exact shape, parse it from local y/m/d components (safe -
    // avoids the UTC-midnight interpretation `new Date("yyyy-mm-dd")`
    // gives, which can shift the date by a day depending on timezone).
    // Otherwise fall back to plain `new Date(v)`, which parses other
    // common formats (e.g. "MM/DD/YYYY") in local time already, so there
    // is no equivalent UTC pitfall for those.
    function parseDateInputValueLoose(v) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
            var parts = v.split('-');
            return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        }
        return new Date(v);
    }

    var MAX_GUESTS = 100;

    // Every field maps to: the formData key, the data-group used to flag
    // the field visually on error, whether it's required, and an optional
    // format test. `required` may also be a function of the current
    // formData, for fields whose requiredness depends on another field
    // (roomAlcoveSelection only matters when spaceCategory needs it).
    var FIELDS = [
        { key: 'firstName', group: 'firstName', required: true, message: 'First name is required.' },
        { key: 'lastName', group: 'lastName', required: true, message: 'Last name is required.' },
        {
            key: 'phone', group: 'phone', required: true, message: 'Phone number is required.',
            formatTest: function (v) { return PHONE_REGEX.test(v); },
            formatMessage: 'Please enter a valid phone number.'
        },
        {
            key: 'email', group: 'email', required: true, message: 'Email is required.',
            formatTest: function (v) { return EMAIL_REGEX.test(v); },
            formatMessage: 'Please enter a valid email address.'
        },
        {
            key: 'spaceCategory', group: 'spaceCategory', required: true,
            message: 'Please choose a space category.'
        },
        {
            key: 'roomAlcoveSelection', group: 'roomAlcoveSelection',
            required: function (data) { return hasSecondaryDropdown(data.spaceCategory); },
            message: 'Please make a selection.'
        },
        {
            key: 'guests', group: 'guests', required: true, message: 'Please enter the number of guests.',
            formatTest: function (v) { return Number(v) >= 1 && Number(v) <= MAX_GUESTS; },
            formatMessage: 'Please enter a number of guests between 1 and ' + MAX_GUESTS + '.'
        },
        {
            key: 'date', group: 'date', required: true, message: 'Please select a date.',
            // Compares actual Date objects (normalized to midnight), not
            // raw strings - a string comparison silently breaks if the
            // input's real value isn't exactly "yyyy-mm-dd", which this
            // environment has not reliably guaranteed.
            formatTest: function (v) {
                var picked = parseDateInputValueLoose(v);
                if (isNaN(picked.getTime())) { return false; }
                picked.setHours(0, 0, 0, 0);
                return picked.getTime() >= getMinDateObj().getTime() && picked.getTime() <= getMaxDateObj().getTime();
            },
            formatMessage: 'Please select a date between today and 2 months from now.'
        },
        { key: 'city', group: 'city', required: true, message: 'City is required.' },
        { key: 'zip', group: 'zip', required: true, message: 'Zip code is required.' },
        { key: 'comments', group: 'comments', required: false },
        {
            key: 'marketingConsent', group: 'marketingConsent', required: true, isCheckbox: true,
            message: 'This needs to be checked to continue.'
        },
        {
            key: 'dataConsent', group: 'dataConsent', required: true, isCheckbox: true,
            message: 'This needs to be checked to continue.'
        }
    ];

    // ===== CONSTRUCTOR =====
    function GeneralInquiryMainView(options) {
        SCView.call(this);
        self = this;
        self.options = options || {};
        self.template = general_inquiry_tpl;

        self.model = new GeneralInquiryModel();
        self.isSubmitting = false;
        self.formData = {
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            spaceCategory: '',
            roomAlcoveSelection: '',
            guests: '',
            date: '',
            dateDisplay: '',
            city: '',
            zip: '',
            comments: '',
            marketingConsent: false,
            dataConsent: false
        };

        // Which month the custom calendar panel is currently showing.
        // Starts on the current month; navigation moves this without
        // touching formData.date until an actual day is clicked.
        var today = new Date();
        self.calendarViewYear = today.getFullYear();
        self.calendarViewMonth = today.getMonth();

        // Click-outside-to-close for the custom date panel. Bound on
        // document (not the view's own delegated events) since it needs
        // to catch clicks anywhere on the page, not just inside this view.
        jQuery(document).on('click.giDatepicker', function (e) {
            if (!jQuery(e.target).closest('.js-gi-datepicker').length) {
                self._closeDatePanel();
            }
        });
    }

    GeneralInquiryMainView.prototype = Object.create(SCView.prototype);
    GeneralInquiryMainView.prototype.constructor = GeneralInquiryMainView;

    GeneralInquiryMainView.prototype.getEvents = function () {
        return {
            'change .js-gi-input': 'onFieldChange',
            'keyup .js-gi-input': 'onFieldChange',
            'change .js-gi-checkbox': 'onCheckboxChange',
            'change .js-gi-space-category': 'onSpaceCategoryChange',
            'click .js-gi-date-display': 'onDateInputClick',
            'click .js-gi-date-prev': 'onDatePrevClick',
            'click .js-gi-date-next': 'onDateNextClick',
            'click .js-gi-date-grid': 'onDateGridClick',
            'click [data-action="submit"]': 'onSubmitClick'
        };
    };

    GeneralInquiryMainView.prototype.getContext = function () {
        // The secondary (Meeting Room / Alcove) dropdown starts hidden and
        // empty in the template itself - it's built and shown entirely via
        // DOM manipulation (see _updateSecondaryDropdown), not re-rendered
        // through Handlebars, since its options depend on a value that
        // only exists at runtime.
        return {
            selected: self.formData,
            isSubmitting: self.isSubmitting,
            maxGuests: MAX_GUESTS
        };
    };

    GeneralInquiryMainView.prototype.onFieldChange = function (e) {
        var $el = self.$(e.currentTarget);
        var field = $el.data('field');
        if (!field) { return; }
        self.formData[field] = $el.val();
        self._clearGroupError(field);
        self._hideBanners();
    };

    GeneralInquiryMainView.prototype.onCheckboxChange = function (e) {
        var $el = self.$(e.currentTarget);
        var field = $el.data('field');
        if (!field) { return; }
        self.formData[field] = $el.is(':checked');
        self._clearGroupError(field);
        self._hideBanners();
    };

    // When Space Category changes, rebuild the secondary dropdown's
    // options in place (rather than re-rendering the whole view, which
    // would mean relying on unverified render-lifecycle/event-rebinding
    // behavior). Any previous secondary selection is cleared since it no
    // longer applies to the new category.
    GeneralInquiryMainView.prototype.onSpaceCategoryChange = function (e) {
        var $el = self.$(e.currentTarget);
        self.formData.spaceCategory = $el.val();
        self.formData.roomAlcoveSelection = '';
        self._clearGroupError('spaceCategory');
        self._clearGroupError('roomAlcoveSelection');
        self._hideBanners();
        self._updateSecondaryDropdown();
    };

    GeneralInquiryMainView.prototype._updateSecondaryDropdown = function () {
        var category = self.formData.spaceCategory;
        var $group = self.$('[data-group="roomAlcoveSelection"]');
        var $select = $group.find('.js-gi-room-alcove-select');
        var $label = $group.find('.js-gi-room-alcove-label');

        if (!hasSecondaryDropdown(category)) {
            $group.hide();
            $select.empty();
            return;
        }

        var meta = SECONDARY_LABELS[category];
        var options = ROOM_ALCOVE_OPTIONS[category];

        $label.text(meta.label);
        $select.empty();
        $select.append(jQuery('<option></option>').attr('value', '').text(meta.placeholder));
        options.forEach(function (optionText) {
            $select.append(jQuery('<option></option>').attr('value', optionText).text(optionText));
        });
        $group.show();
    };

    // ===== CUSTOM DATE PICKER =====
    // Built in-house rather than relying on the native <input type="date">
    // picker: this environment does not reliably render/enforce that
    // picker's min/max restriction (out-of-range dates stayed clickable),
    // so a fully custom widget is the only way to guarantee disabled days
    // are genuinely disabled.

    var MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];

    function getMinDateObj() {
        var d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }

    function getMaxDateObj() {
        var d = new Date();
        d.setMonth(d.getMonth() + 2);
        d.setHours(0, 0, 0, 0);
        return d;
    }

    function formatDisplayDate(year, month, day) {
        var mm = (month + 1) < 10 ? '0' + (month + 1) : String(month + 1);
        var dd = day < 10 ? '0' + day : String(day);
        return mm + '/' + dd + '/' + year;
    }

    GeneralInquiryMainView.prototype.onDateInputClick = function (e) {
        e.stopPropagation();
        var $panel = self.$('.js-gi-date-panel');
        var isOpen = $panel.hasClass('is-open');
        self._closeDatePanel();
        if (!isOpen) {
            // Reopen on whichever month the current selection is in, or
            // today's month if nothing is selected yet.
            if (self.formData.date) {
                var parts = self.formData.date.split('-');
                self.calendarViewYear = Number(parts[0]);
                self.calendarViewMonth = Number(parts[1]) - 1;
            }
            self._renderDatePanel();
            $panel.addClass('is-open');
        }
    };

    GeneralInquiryMainView.prototype._closeDatePanel = function () {
        self.$('.js-gi-date-panel').removeClass('is-open');
    };

    GeneralInquiryMainView.prototype.onDatePrevClick = function (e) {
        e.stopPropagation();
        self.calendarViewMonth -= 1;
        if (self.calendarViewMonth < 0) {
            self.calendarViewMonth = 11;
            self.calendarViewYear -= 1;
        }
        self._renderDatePanel();
    };

    GeneralInquiryMainView.prototype.onDateNextClick = function (e) {
        e.stopPropagation();
        self.calendarViewMonth += 1;
        if (self.calendarViewMonth > 11) {
            self.calendarViewMonth = 0;
            self.calendarViewYear += 1;
        }
        self._renderDatePanel();
    };

    GeneralInquiryMainView.prototype.onDateGridClick = function (e) {
        e.stopPropagation();
        var $day = self.$(e.target).closest('.gi-datepicker-day');
        if (!$day.length || $day.prop('disabled') || $day.hasClass('is-empty')) { return; }

        var day = Number($day.attr('data-day'));
        self._selectDate(self.calendarViewYear, self.calendarViewMonth, day);
        self._closeDatePanel();
    };

    GeneralInquiryMainView.prototype._selectDate = function (year, month, day) {
        self.formData.date = formatLocalDate(new Date(year, month, day));
        self.formData.dateDisplay = formatDisplayDate(year, month, day);
        self.$('.js-gi-date-display').val(self.formData.dateDisplay);
        self._clearGroupError('date');
        self._hideBanners();
    };

    GeneralInquiryMainView.prototype._renderDatePanel = function () {
        var min = getMinDateObj();
        var max = getMaxDateObj();
        var viewYear = self.calendarViewYear;
        var viewMonth = self.calendarViewMonth;

        self.$('.js-gi-date-label').text(MONTH_NAMES[viewMonth] + ' ' + viewYear);

        // Lock navigation at the boundary months so visitors can't browse
        // into a month that's entirely out of range.
        var atMinMonth = (viewYear === min.getFullYear() && viewMonth === min.getMonth());
        var atMaxMonth = (viewYear === max.getFullYear() && viewMonth === max.getMonth());
        self.$('.js-gi-date-prev').prop('disabled', atMinMonth);
        self.$('.js-gi-date-next').prop('disabled', atMaxMonth);

        var firstOfMonth = new Date(viewYear, viewMonth, 1);
        var startOffset = firstOfMonth.getDay(); // 0 (Sun) - 6 (Sat)
        var daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

        var today = new Date();
        today.setHours(0, 0, 0, 0);

        var selected = null;
        if (self.formData.date) {
            var parts = self.formData.date.split('-');
            selected = { y: Number(parts[0]), m: Number(parts[1]) - 1, d: Number(parts[2]) };
        }

        var html = '';
        var i;

        for (i = 0; i < startOffset; i++) {
            html += '<span class="gi-datepicker-day is-empty"></span>';
        }

        for (var day = 1; day <= daysInMonth; day++) {
            var cellDate = new Date(viewYear, viewMonth, day);
            var isDisabled = cellDate.getTime() < min.getTime() || cellDate.getTime() > max.getTime();
            var isToday = cellDate.getTime() === today.getTime();
            var isSelected = selected && selected.y === viewYear && selected.m === viewMonth && selected.d === day;

            var classes = 'gi-datepicker-day';
            if (isToday) { classes += ' is-today'; }
            if (isSelected) { classes += ' is-selected'; }

            html += '<button type="button" class="' + classes + '" data-day="' + day + '"'
                + (isDisabled ? ' disabled' : '') + '>' + day + '</button>';
        }

        self.$('.js-gi-date-grid').html(html);
    };

    // ===== VALIDATION =====

    GeneralInquiryMainView.prototype._clearGroupError = function (group) {
        self.$('[data-group="' + group + '"]').removeClass('has-error');
    };

    GeneralInquiryMainView.prototype._showGroupError = function (group, message) {
        var $group = self.$('[data-group="' + group + '"]');
        $group.addClass('has-error');
        if (message) {
            $group.find('.gi-error').text(message);
        }
    };

    GeneralInquiryMainView.prototype.validateForm = function () {
        var firstInvalidEl = null;

        FIELDS.forEach(function (cfg) {
            self._clearGroupError(cfg.group);
        });

        FIELDS.forEach(function (cfg) {
            // roomAlcoveSelection is invisible (and irrelevant) for
            // Workstation - skip it entirely rather than flag a
            // field the user can't even see.
            if (cfg.key === 'roomAlcoveSelection' && !hasSecondaryDropdown(self.formData.spaceCategory)) {
                return;
            }

            var isRequired = typeof cfg.required === 'function' ? cfg.required(self.formData) : cfg.required;
            var value = self.formData[cfg.key];

            if (cfg.isCheckbox) {
                if (isRequired && value !== true) {
                    self._showGroupError(cfg.group, cfg.message);
                    if (!firstInvalidEl) { firstInvalidEl = self.$('[data-group="' + cfg.group + '"]'); }
                }
                return;
            }

            var empty = isEmpty(value);

            if (isRequired && empty) {
                self._showGroupError(cfg.group, cfg.message);
                if (!firstInvalidEl) { firstInvalidEl = self.$('[data-group="' + cfg.group + '"]'); }
                return;
            }

            if (!empty && cfg.formatTest && !cfg.formatTest(value)) {
                self._showGroupError(cfg.group, cfg.formatMessage);
                if (!firstInvalidEl) { firstInvalidEl = self.$('[data-group="' + cfg.group + '"]'); }
            }
        });

        if (firstInvalidEl && firstInvalidEl.length) {
            firstInvalidEl[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
            return false;
        }
        return true;
    };

    // ===== BANNERS =====

    GeneralInquiryMainView.prototype._hideBanners = function () {
        self.$('.js-gi-success').removeClass('is-visible');
        self.$('.js-gi-error').removeClass('is-visible');
    };

    GeneralInquiryMainView.prototype._showSuccess = function () {
        self.$('.js-gi-error').removeClass('is-visible');
        self.$('.js-gi-success').addClass('is-visible');

        // Scroll the success message into view so the visitor sees
        // confirmation immediately - without this, on a long form they'd
        // still be looking at wherever they last scrolled (e.g. down by
        // the Submit button) and might not realize it went through.
        var successEl = self.$('.js-gi-success')[0];
        if (successEl && successEl.scrollIntoView) {
            successEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    GeneralInquiryMainView.prototype._showError = function (message) {
        self.$('.js-gi-success').removeClass('is-visible');
        if (message) { self.$('.js-gi-error').text(message); }
        self.$('.js-gi-error').addClass('is-visible');
    };

    GeneralInquiryMainView.prototype._setSubmitting = function (submitting) {
        self.isSubmitting = submitting;
        var $btn = self.$('[data-action="submit"]');
        $btn.prop('disabled', submitting);
        $btn.toggleClass('is-loading', submitting);
    };

    GeneralInquiryMainView.prototype._resetForm = function () {
        self.formData = {
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            spaceCategory: '',
            roomAlcoveSelection: '',
            guests: '',
            date: '',
            dateDisplay: '',
            city: '',
            zip: '',
            comments: '',
            marketingConsent: false,
            dataConsent: false
        };
        self.$('.js-gi-input').val('');
        self.$('.js-gi-checkbox').prop('checked', false);
        self.$('.js-gi-date-display').val('');
        self._closeDatePanel();
        var today = new Date();
        self.calendarViewYear = today.getFullYear();
        self.calendarViewMonth = today.getMonth();
        self._updateSecondaryDropdown();
    };

    // ===== SUBMIT =====

    GeneralInquiryMainView.prototype.onSubmitClick = function (e) {
        e.preventDefault();
        if (self.isSubmitting) { return; }

        if (!self.validateForm()) { return; }

        self._setSubmitting(true);
        self._hideBanners();

        // Fetch a fresh reCAPTCHA v3 token right before submitting (tokens
        // are short-lived and single-use, so this can't be done earlier
        // and cached). If the script/site key isn't set up correctly,
        // proceed WITHOUT a token rather than blocking the visitor
        // entirely - the backend's own placeholder-aware check handles a
        // missing token the same permissive way while it's still being
        // configured (see RECAPTCHA_SECRET_KEY in the backend Model.js).
        //
        // .done()/.fail() (not .then()) on the jQuery Deferred wrapper
        // deliberately, since jQuery's .then() doesn't reliably assimilate
        // a native Promise (what grecaptcha.execute() returns) across all
        // jQuery versions - .done() only ever receives plain values, so
        // there's nothing to assimilate.
        loadRecaptchaScript().done(function () {
            window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: RECAPTCHA_ACTION })
                .then(
                    function (token) { self._submitWithToken(token); },
                    function () { self._submitWithToken(null); }
                );
        }).fail(function () {
            self._submitWithToken(null);
        });
    };

    GeneralInquiryMainView.prototype._submitWithToken = function (recaptchaToken) {
        // roomAlcoveSelection is only meaningful for Meeting Room/Alcove/
        // Events - send an empty string for Workstation rather than
        // whatever stale value might be sitting in formData.
        var roomAlcoveSelection = hasSecondaryDropdown(self.formData.spaceCategory)
            ? self.formData.roomAlcoveSelection
            : '';

        var payload = {
            firstName: self.formData.firstName,
            lastName: self.formData.lastName,
            phone: self.formData.phone,
            email: self.formData.email,
            spaceCategory: self.formData.spaceCategory,
            roomAlcoveSelection: roomAlcoveSelection,
            guests: self.formData.guests,
            // Sent as MM/DD/YYYY (self.formData.dateDisplay), not the
            // internal ISO yyyy-mm-dd value - this NetSuite account's
            // server-side Date.parse() has not reliably accepted strict
            // ISO date-only strings, even though they're spec-valid.
            // MM/DD/YYYY is the format that has actually worked here
            // historically (from the original native date input), so the
            // backend keeps expecting that shape.
            date: self.formData.dateDisplay,
            city: self.formData.city,
            zip: self.formData.zip,
            comments: self.formData.comments,
            marketingConsent: self.formData.marketingConsent,
            dataConsent: self.formData.dataConsent,
            recaptchaToken: recaptchaToken,
            recaptchaAction: RECAPTCHA_ACTION
        };

        self.model.save(payload, {
            type: 'POST',
            success: function (model, response) {
                self._setSubmitting(false);
                if (response && response.success && response.data && response.data.internalId) {
                    // Fire BEFORE _resetForm, which clears formData. Values
                    // come from the payload actually saved, not live form state.
                    pushDataLayer('form_submit_success', {
                        space_category: payload.spaceCategory,
                        space_option: payload.roomAlcoveSelection || '(none)',
                        guest_count: Number(payload.guests) || 0,
                        has_comments: !isEmpty(payload.comments)
                    });
                    self._resetForm();
                    self._showSuccess();
                } else {
                    pushDataLayer('form_submit_error', { error_type: 'server_rejected' });
                    self._showError((response && response.message) || 'Something went wrong. Please try again.');
                }
            },
            error: function (model, xhr) {
                self._setSubmitting(false);
                pushDataLayer('form_submit_error', {
                    error_type: 'request_failed',
                    http_status: (xhr && xhr.status) || 0
                });
                var message = 'Something went wrong. Please try again.';
                try {
                    var parsed = JSON.parse(xhr.responseText);
                    if (parsed && parsed.message) { message = parsed.message; }
                } catch (err) { /* keep default */ }
                self._showError(message);
            }
        });
    };

    return GeneralInquiryMainView;
});
